/**
 * UserDocuments.js
 * Component for managing documents within user profile
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    Table,
    Button,
    Badge,
    Alert,
    Modal,
    Form,
    Row,
    Col,
    Spinner,
    Pagination
} from 'react-bootstrap';
import moment from 'moment';
import { documentService } from '../../services/api';
import { uploadService } from '../../services/uploadService';
import GenericFileUpload from '../Common/GenericFileUpload';
import DocumentDataModal from '../Common/DocumentDataModal';

const UserDocuments = ({ userId }) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingDocument, setEditingDocument] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'general', 'personal', 'administrative', 'medical'
    const [documentTypeFilter, setDocumentTypeFilter] = useState(''); // Filtro testuale per tipo documento
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 5
    });

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        documentType: '',
        category: 'general'
    });

    // File upload state
    const [fileData, setFileData] = useState(null);
    const [uploadError, setUploadError] = useState('');

    // Stato per il modal unificato di creazione documento
    const [showDataModal, setShowDataModal] = useState(false);

    // Load documents for this user
    const loadDocuments = useCallback(async (page = pagination.currentPage) => {
        try {
            setLoading(true);
            const response = await documentService.getUserDocuments(
                userId,
                categoryFilter,
                documentTypeFilter,
                page,
                pagination.limit
            );

            if (response.success) {
                setDocuments(response.data);
                setPagination(prev => ({
                    ...prev,
                    currentPage: response.pagination.currentPage,
                    totalPages: response.pagination.totalPages,
                    totalCount: response.pagination.totalCount
                }));
            } else {
                setError('Errore nel caricamento dei documenti');
            }
        } catch (err) {
            setError('Errore di connessione al server');
            console.error('Error loading documents:', err);
        } finally {
            setLoading(false);
        }
    }, [userId, categoryFilter, documentTypeFilter, pagination.limit, pagination.currentPage]);

    useEffect(() => {
        if (userId) {
            // Reset to page 1 when filter changes
            setPagination(prev => ({ ...prev, currentPage: 1 }));
            loadDocuments(1);
        }
    }, [userId, categoryFilter, loadDocuments]);

    // Handle page changes
    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, currentPage: newPage }));
        loadDocuments(newPage);
    };

    const getCategoryBadge = (category) => {
        const categoryConfig = {
            general: { label: 'Generale', variant: 'secondary' },
            personal: { label: 'Personale', variant: 'primary' },
            administrative: { label: 'Amministrativo', variant: 'info' },
            medical: { label: 'Medico', variant: 'success' }
        };

        const config = categoryConfig[category] || categoryConfig.general;
        return <Badge bg={config.variant}>{config.label}</Badge>;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        setError('');
        setSuccess('');
        setUploadError('');

        if (!fileData && !editingDocument) {
            setUploadError('Seleziona un file da caricare');
            return;
        }

        if (!formData.title.trim()) {
            setError('Il titolo è obbligatorio');
            return;
        }

        try {
            const submitData = {
                ...formData,
                userId: userId
            };

            let response;
            if (editingDocument) {
                // Modalità edit - usa la mutation di update unificata
                const updateData = {
                    ...submitData,
                    file: fileData // Può essere null se non c'è un nuovo file
                };
                
                response = await uploadService.updateDocument(editingDocument.id, updateData);
            } else {
                // Create new document with file using uploadService
                submitData.file = fileData;
                response = await uploadService.uploadDocument(submitData);
            }

            if (response.success) {
                setSuccess(
                    editingDocument
                        ? 'Documento aggiornato con successo'
                        : 'Documento caricato con successo'
                );
                setShowModal(false);
                resetForm();
                loadDocuments(pagination.currentPage);
            } else {
                setError(response.message || 'Errore nel salvataggio del documento');
            }
        } catch (err) {
            console.error('Error in handleSubmit:', err);
            setError('Errore di connessione al server');
        }
    };

    // Handle delete
    const handleDelete = async (documentId, documentTitle) => {
        if (window.confirm(`Sei sicuro di voler eliminare il documento "${documentTitle}"?`)) {
            try {
                const response = await documentService.deleteDocument(documentId);
                if (response.success) {
                    setSuccess('Documento eliminato con successo');
                    loadDocuments(pagination.currentPage);
                } else {
                    setError(response.message || 'Errore nell\'eliminazione del documento');
                }
            } catch (err) {
                setError('Errore di connessione al server');
                console.error('Error deleting document:', err);
            }
        }
    };

    // Handle download
    const handleDownload = async (doc) => {
        try {
            const response = await documentService.downloadDocument(doc.id);
            if (response.success) {
                // Create download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', doc.fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } else {
                setError('Errore nel download del documento');
            }
        } catch (err) {
            setError('Errore nel download del documento');
            console.error('Error downloading document:', err);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            documentType: '',
            category: 'general'
        });
        setFileData(null);
        setEditingDocument(null);
        setUploadError('');
    };

    // Handle edit
    const handleEdit = (document) => {
        setEditingDocument(document);
        setFormData({
            title: document.title || '',
            description: document.description || '',
            documentType: document.documentType || '',
            category: document.category || 'general'
        });
        setShowModal(true);
    };

    // Handle new document creation
    const handleNewDocument = () => {
        setShowDataModal(true);
    };

    // Gestori per la creazione documento unificata
    const handleDocumentSuccess = (result) => {
        setSuccess('Documento creato con successo');
        setShowDataModal(false);
        loadDocuments(pagination.currentPage);
    };

    const handleDocumentError = (error) => {
        setError(error?.message || error || 'Errore durante la creazione del documento');
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle file upload
    const handleFileUpload = (file, error) => {
        if (error) {
            setUploadError(error?.message || error || 'Errore nel file');
            setFileData(null);
        } else {
            setFileData(file);
            setUploadError('');
        }
    };

    return (
        <div className="user-documents">
            {/* Header with filter and actions */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-3">
                    <Form.Select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        style={{ width: 'auto', minWidth: '160px' }}
                    >
                        <option value="all">Tutte le categorie</option>
                        <option value="general">Generale</option>
                        <option value="personal">Personale</option>
                        <option value="administrative">Amministrativo</option>
                        <option value="medical">Medico</option>
                    </Form.Select>

                    <div className="position-relative">
                        <Form.Control
                            type="text"
                            placeholder="Filtra per tipo documento..."
                            value={documentTypeFilter}
                            onChange={(e) => setDocumentTypeFilter(e.target.value)}
                            style={{ width: 'auto', minWidth: '200px', paddingLeft: '35px' }}
                        />
                        <i 
                            className="fas fa-search position-absolute" 
                            style={{ 
                                left: '10px', 
                                top: '50%', 
                                transform: 'translateY(-50%)', 
                                color: '#6c757d' 
                            }}
                        ></i>
                    </div>

                    {pagination.totalCount > 0 && (
                        <span className="text-muted">
                            {pagination.totalCount} document{pagination.totalCount !== 1 ? 'i' : 'o'}
                        </span>
                    )}
                </div>

                <div className="d-flex gap-2">
                    <Button variant="primary" onClick={handleNewDocument}>
                        <i className="fas fa-plus me-2"></i>
                        Nuovo documento
                    </Button>
                </div>
            </div>

            {/* Alerts */}
            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" onClose={() => setSuccess('')} dismissible>
                    {success}
                </Alert>
            )}

            {/* Documents Table */}
            <Card>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Caricamento documenti...
                        </div>
                    ) : documents.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                            <p className="text-muted">
                                {categoryFilter === 'all'
                                    ? 'Nessun documento presente'
                                    : `Nessun documento nella categoria "${categoryFilter}"`
                                }
                            </p>
                        </div>
                    ) : (
                        <>
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>Titolo</th>
                                        <th>Categoria</th>
                                        <th>Tipo</th>
                                        <th>Data Upload</th>
                                        <th>Dimensione</th>
                                        <th>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documents.map(doc => (
                                        <tr key={doc.id}>
                                            <td>
                                                <div>
                                                    <strong>{doc.title}</strong>
                                                    {doc.description && (
                                                        <div className="text-muted small">{doc.description}</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{getCategoryBadge(doc.category)}</td>
                                            <td>{doc.documentType}</td>
                                            <td>{moment(doc.createdAt).format('DD/MM/YYYY')}</td>
                                            <td>{(doc.fileSize / 1024).toFixed(1)} KB</td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleDownload(doc)}
                                                        title="Scarica documento"
                                                    >
                                                        <i className="fas fa-download"></i>
                                                    </Button>
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={() => handleEdit(doc)}
                                                        title="Modifica documento"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(doc.id, doc.title)}
                                                        title="Elimina documento"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-3">
                                    <Pagination>
                                        <Pagination.First
                                            onClick={() => handlePageChange(1)}
                                            disabled={pagination.currentPage === 1}
                                        />
                                        <Pagination.Prev
                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                            disabled={pagination.currentPage === 1}
                                        />

                                        {[...Array(pagination.totalPages)].map((_, index) => {
                                            const page = index + 1;
                                            return (
                                                <Pagination.Item
                                                    key={page}
                                                    active={page === pagination.currentPage}
                                                    onClick={() => handlePageChange(page)}
                                                >
                                                    {page}
                                                </Pagination.Item>
                                            );
                                        })}

                                        <Pagination.Next
                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                            disabled={pagination.currentPage === pagination.totalPages}
                                        />
                                        <Pagination.Last
                                            onClick={() => handlePageChange(pagination.totalPages)}
                                            disabled={pagination.currentPage === pagination.totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Document Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingDocument ? 'Modifica Documento' : 'Nuovo Documento'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Titolo *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Inserisci il titolo del documento"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Categoria</Form.Label>
                                    <Form.Select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                    >
                                        <option value="general">Generale</option>
                                        <option value="personal">Personale</option>
                                        <option value="administrative">Amministrativo</option>
                                        <option value="medical">Medico</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tipo Documento</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="documentType"
                                        value={formData.documentType}
                                        onChange={handleInputChange}
                                        placeholder="Inserisci il tipo di documento"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Descrizione</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Descrizione opzionale del documento"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>File {editingDocument ? '(sostituisci file esistente)' : '*'}</Form.Label>
                            <GenericFileUpload
                                onFileSelect={handleFileUpload}
                                acceptedTypes={['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                                maxSize={10 * 1024 * 1024} // 10MB
                            />
                            {fileData && (
                                <div className="mt-2 p-2 bg-light rounded">
                                    <i className="fas fa-file me-2"></i>
                                    {fileData.name} 
                                    <small className="text-muted">
                                        ({(fileData.size / 1024 / 1024).toFixed(2)} MB)
                                    </small>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="text-danger p-0 ms-2"
                                        onClick={() => setFileData(null)}
                                        title="Rimuovi file"
                                    >
                                        <i className="fas fa-times"></i>
                                    </Button>
                                </div>
                            )}
                            {uploadError && (
                                <div className="text-danger small mt-1">{uploadError}</div>
                            )}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Annulla
                        </Button>
                        <Button type="submit" variant="primary">
                            {editingDocument ? 'Aggiorna' : 'Carica'} Documento
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal per creare nuovo documento */}
            <DocumentDataModal
                show={showDataModal}
                onHide={() => setShowDataModal(false)}
                onSuccess={handleDocumentSuccess}
                onError={handleDocumentError}
                userId={userId}
            />
        </div>
    );
};

export default UserDocuments;
